import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { recordReferralClick, storeReferralCode, validateReferralCode } from '@/utils/AffiliateUtils';
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { useIsMobile } from '@/hooks/use-mobile';

export function ReferralHandler() {
  const location = useLocation();
  const { code } = useParams<{ code: string }>();
  const { session } = useAuthSession();
  const { isMobile } = useIsMobile();

  // Process referral from URL parameters if present
  useEffect(() => {
    const processReferralFromUrl = async () => {
      // Different ways to get referral code: URL path, query parameter, or localStorage
      let referralCode: string | null = null;
      let source = '';
      
      // Check for referral code in the URL path (/refer/CODE)
      if (code) {
        referralCode = code;
        source = 'path';
        console.log('Found referral code in path:', code);
      } else {
        // Otherwise check for referral code in query parameters (?ref=CODE or ?referral=CODE)
        const searchParams = new URLSearchParams(location.search);
        referralCode = searchParams.get('ref') || searchParams.get('referral');
        source = referralCode ? 'query' : '';
        console.log('Checking query params for referral code:', referralCode);
      }
      
      if (referralCode) {
        handleReferralCode(referralCode, source);
      } else {
        console.log('No referral code found in URL');
      }
    };
    
    processReferralFromUrl();
  }, [location, code, session?.user?.id]);
  
  const handleReferralCode = async (referralCode: string, source: string = 'unknown') => {
    try {
      console.log('Processing referral code:', referralCode, 'from source:', source);
      
      // Skip if the user is the referrer (can't refer themselves)
      if (session?.user) {
        // This is a simple client-side check, but we also have server-side validation
        try {
          const { data } = await supabase
            .from('affiliates')
            .select('user_id')
            .ilike('referral_code', referralCode)
            .maybeSingle();
            
          if (data && data.user_id === session.user.id) {
            console.log('User cannot refer themselves');
            toast.error("You cannot use your own referral code", {
              id: "self-referral-error",
              duration: 3000
            });
            return;
          }
        } catch (error) {
          console.error('Error checking affiliate user:', error);
          // Continue with validation even if this check fails
        }
      }
      
      // Validate referral code format
      if (!/^[a-zA-Z0-9]{4,20}$/.test(referralCode)) {
        console.log('Invalid referral code format:', referralCode);
        return;
      }
      
      // Validate referral code exists in database
      const isValid = await validateReferralCode(referralCode);
      
      if (isValid) {
        console.log('Valid referral code detected:', referralCode);
        
        // Store the code in localStorage for later use during checkout
        storeReferralCode(referralCode);
        
        // Record the click (analytics)
        try {
          await recordReferralClick(referralCode);
          console.log('Referral click recorded successfully');
        } catch (clickError) {
          console.error('Failed to record referral click:', clickError);
          // Non-critical error, we can continue
        }
        
        // Show a small toast to acknowledge the referral
        toast.success("Welcome! You're using a referral link", {
          id: "referral-detected",
          duration: isMobile ? 2000 : 3000,
          position: isMobile ? "bottom-center" : "top-right"
        });
      } else {
        console.log('Invalid referral code:', referralCode);
        toast.error("The referral code appears to be invalid or expired", {
          id: "invalid-referral-code",
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Error processing referral code:', error);
      toast.error("There was a problem processing the referral code", { 
        id: "referral-error" 
      });
    }
  };

  // This is a utility component that doesn't render anything
  return null;
}

export default ReferralHandler;
